/**
 * Quest Audio Preloader
 * Watches for intro state and specific quests becoming active and preloads their audio assets
 *
 * NOTE: This function MUST be called within a Vue component's setup context
 * (e.g., inside setup() or <script setup>) for watchers to work correctly.
 */

import {
	AudioNames,
	AudioSpriteNames,
} from "@/game/entities/config/audio/audioConfig";
import type { ComputedRef, Ref, WatchStopHandle } from "vue";
import { watch } from "vue";
import { AudioLazyLoader } from "../game/AudioLazyLoader";
import type { QuestActive } from "../types/quest";

/**
 * Setup watchers to preload quest-specific and intro audio
 * - Intro: Loads intro music + cutscene audio when isIntroActive = true
 * - Quest 35: Requires CUTSCENES_OUTRO_CHAPTER_1 + INTRO_MUSIC
 * - Quest 70: Requires CUTSCENES_OUTRO_CHAPTER_2 + INTRO_MUSIC
 * - Quest 105: Requires CUTSCENES_OUTRO_CHAPTER_3 + INTRO_MUSIC
 *
 * @returns Cleanup function to stop all watchers
 */
export function setupQuestAudioPreloading(
	activeQuests: Ref<Array<QuestActive>>,
	isIntroActive: ComputedRef<boolean>,
): () => void {
	const lazyLoader = AudioLazyLoader.getInstance();
	const stopHandles: Array<WatchStopHandle> = [];

	// Watch for intro becoming active - load intro music + cutscene audio
	// This ensures audio is ready BEFORE intro starts playing
	const stopIntroWatch = watch(isIntroActive, (isActive: boolean): void => {
		if (isActive) {
			// Preload both intro music and cutscene audio
			Promise.all([
				lazyLoader.loadAudio(AudioNames.INTRO_MUSIC),
				lazyLoader.loadAudio(AudioSpriteNames.CUTSCENES_INTRO),
			]).catch((err: Error): void => {
				console.error("Failed to preload intro audio:", err);
			});
		}
	});
	stopHandles.push(stopIntroWatch);

	// Watch for quest 35 (Chapter 1 outro)
	// Also needs intro music for the outro sequence
	const stopQuest35Watch = watch(
		activeQuests,
		(quests: Array<QuestActive>): void => {
			const hasQuest35 = quests.some(
				(q: QuestActive): boolean => q.id === 35,
			);
			if (hasQuest35) {
				// Preload Chapter 1 outro audio + intro music (used in outros too)
				Promise.all([
					lazyLoader.loadAudio(
						AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_1,
					),
					lazyLoader.loadAudio(AudioNames.INTRO_MUSIC),
				]).catch((err: Error): void => {
					console.error(
						"Failed to preload Chapter 1 outro audio:",
						err,
					);
				});
			}
		},
	);
	stopHandles.push(stopQuest35Watch);

	// Watch for quest 70 (Chapter 2 outro)
	// Also needs intro music for the outro sequence
	const stopQuest70Watch = watch(
		activeQuests,
		(quests: Array<QuestActive>): void => {
			const hasQuest70 = quests.some(
				(q: QuestActive): boolean => q.id === 70,
			);
			if (hasQuest70) {
				// Preload Chapter 2 outro audio + intro music (used in outros too)
				Promise.all([
					lazyLoader.loadAudio(
						AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_2,
					),
					lazyLoader.loadAudio(AudioNames.INTRO_MUSIC),
				]).catch((err: Error): void => {
					console.error(
						"Failed to preload Chapter 2 outro audio:",
						err,
					);
				});
			}
		},
	);
	stopHandles.push(stopQuest70Watch);
	// Watch for quest 105 (Chapter 3 outro)
	// Also needs intro music for the outro sequence
	const stopQuest105Watch = watch(
		activeQuests,
		(quests: Array<QuestActive>): void => {
			const hasQuest105 = quests.some(
				(q: QuestActive): boolean => q.id === 105,
			);
			if (hasQuest105) {
				// Preload Chapter 3 outro audio + intro music (used in outros too)
				Promise.all([
					lazyLoader.loadAudio(
						AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_3,
					),
					lazyLoader.loadAudio(AudioNames.INTRO_MUSIC),
				]).catch((err: Error): void => {
					console.error(
						"Failed to preload Chapter 3 outro audio:",
						err,
					);
				});
			}
		},
	);
	stopHandles.push(stopQuest105Watch);

	// Return cleanup function to stop all watchers
	return (): void => {
		stopHandles.forEach((stop: WatchStopHandle): void => stop());
	};
}
